import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { API } from '@/lib/data';
import { DEFAULT_SERVICES, DEFAULT_PROCESS_STEPS, DEFAULT_VIDEOS, DEFAULT_BLOGS, DEFAULT_NEWS, DEFAULT_CERTIFICATES, DEFAULT_FAQ } from '@/lib/defaults';
import { Reveal, HoverCard, FloatingElement, AnimatedCounter } from '@/components/Reveal';
import { formatDateTR } from '@/lib/helpers';
import { useSettings } from '@/lib/useSettings';
import { formatWorkingHours } from '@/lib/workingHours';
import { SEO, createMedicalBusinessSchema, createFAQSchema, createWebSiteSchema, createVideoListSchema } from '@/components/SEO';
import { ServiceCard } from '@/components/ServiceCard';
import { ContactForm } from '@/components/ContactForm';
import { HealthAssessment } from '@/components/HealthAssessment';
import { ServiceCardSkeleton, CardSkeleton, ProcessSkeleton, FAQSkeleton } from '@/components/Skeletons';
import { OptimizedImage } from '@/components/OptimizedImage';
import {
  ArrowRight, Play, Clock, MapPin,
  MessageCircle, ClipboardList, FileText, TrendingUp,
  GraduationCap, Award, BookOpen, ChevronLeft, ChevronRight,
  Tv, Mic, Users, Mail, Sparkles, ShieldCheck, BadgeCheck, Heart, Star, Stethoscope, Beaker, Medal, Atom, Brain, Microscope,
  Youtube, Instagram, Facebook,
  icons as allLucideIcons
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const processIcons = { MessageCircle, ClipboardList, FileText, TrendingUp };
const certIcons = { Diploma: GraduationCap, Sertifika: Award, Akademik: BookOpen };
const getBannerIcon = (name) => allLucideIcons[name] || Award;

export default function HomePage() {
  const settings = useSettings();
  const [services, setServices] = useState(DEFAULT_SERVICES);
  const [processSteps, setProcessSteps] = useState(DEFAULT_PROCESS_STEPS);
  const [videos, setVideos] = useState(DEFAULT_VIDEOS);
  const [blogs, setBlogs] = useState(DEFAULT_BLOGS);
  const [news, setNews] = useState(DEFAULT_NEWS);
  const [certificates, setCertificates] = useState(DEFAULT_CERTIFICATES);
  const [faq, setFaq] = useState(DEFAULT_FAQ);

  useEffect(() => {
    axios.get(`${API}/homepage-data`).then(r => {
      const d = r.data;
      if (d.services?.length) setServices(d.services);
      if (d.process_steps?.length) setProcessSteps(d.process_steps);
      if (d.videos?.length) setVideos(d.videos);
      if (d.blogs?.length) setBlogs(d.blogs);
      if (d.news?.length) setNews(d.news);
      if (d.certificates?.length) setCertificates(d.certificates);
      if (d.faq?.length) setFaq(d.faq);
    }).catch(() => {});
  }, []);

  const homeSchema = createMedicalBusinessSchema(settings);
  const faqSchema = createFAQSchema(faq);
  const websiteSchema = createWebSiteSchema();
  const videoListSchema = createVideoListSchema(videos);

  return (
    <div data-testid="home-page" className="overflow-x-hidden">
      <SEO
        title="Ana Sayfa"
        description={settings?.hero?.subtitle || 'Prof. Dr. Nilüfer Yıldırım Fonksiyonel Tıp Kliniği - Sağlığınıza bütüncül yaklaşım, kişiye özel çözümler. Hormonal antiaging, koruyucu hekimlik ve stres yönetimi.'}
        schema={[homeSchema, faqSchema, websiteSchema, videoListSchema]}
      />
      {/* B) Hero Section */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FBFAF7] via-[#F5F3EF] to-[#FBFAF7]" />
        <motion.div
          className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-[#1F6E7A]/5 blur-[120px]"
          animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.8, 0.5] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 sm:py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <Reveal variant="fadeLeft" duration={0.8} delay={100}>
                <h1 className="font-heading text-[42px] sm:text-[46px] lg:text-[46px] font-semibold tracking-[-0.02em] text-[#1F2937] mb-5 leading-[1.1]">
                  {settings.hero?.title || 'Fonksiyonel tıp yaklaşımı ve stres yönetimi ile kişiye özel çözümler'}
                </h1>
              </Reveal>
              <Reveal variant="fadeLeft" duration={0.7} delay={250}>
                <p className="text-[22px] sm:text-[24px] text-[#5F6B76] leading-relaxed mb-8 max-w-lg">
                  {settings.hero?.subtitle || 'Amacımız bedensel ve zihinsel iyilik haline ulaşmak için SADE (Sağlıklı, Aktif, Dengeli, Esnek) bir rehberlik'}
                </p>
              </Reveal>
              <Reveal variant="fadeUp" delay={400}>
                <div className="flex flex-wrap gap-3">
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Link
                      to="/iletisim"
                      className="inline-flex items-center px-7 py-3.5 rounded-full bg-[#1F6E7A] text-white text-base font-semibold shadow-[0_8px_24px_-12px_rgba(31,110,122,0.4)] hover:bg-[#185960] transition-colors"
                      data-testid="hero-primary-cta-button"
                    >
                      Randevu Al
                    </Link>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Link
                      to="/hizmetler"
                      className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#F5F3EF] border border-[#E7E5E4] text-[#1F2937] text-base font-semibold transition-colors"
                      data-testid="hero-secondary-cta-button"
                    >
                      Tedavileri İncele <ArrowRight size={18} />
                    </Link>
                  </motion.div>
                </div>
              </Reveal>
              {/* Trust metrics with animated counters */}
              <Reveal variant="fadeUp" delay={550}>
                <div className="flex flex-wrap gap-8 mt-8">
                  <div className="flex items-center gap-3">
                    <motion.div
                      className="w-10 h-10 rounded-full bg-[#D8C7A6]/20 flex items-center justify-center"
                      whileHover={{ rotate: -15, scale: 1.15 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <BookOpen size={20} className="text-[#D8C7A6]" />
                    </motion.div>
                    <span className="text-[22px] text-[#5F6B76]">
                      <strong className="text-[#1F2937] text-lg">{settings.hero?.experience_years || '20+'}</strong> Yıl Deneyim
                    </span>
                  </div>
                </div>
              </Reveal>
            </div>
            <Reveal variant="scaleUp" duration={1} delay={200} className="order-1 lg:order-2">
              <div className="flex flex-col items-center">
                <div className="relative">
                  <motion.div
                    className="absolute -inset-4 rounded-full bg-[#D8C7A6]/15 blur-2xl"
                    animate={{ scale: [1, 1.08, 1] }}
                    transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                  />
                  <FloatingElement yOffset={6} duration={5}>
                    <OptimizedImage
                      src={settings.doctor?.image}
                      alt="Prof. Dr. Nilüfer Yıldırım"
                      className="relative w-72 h-72 sm:w-80 sm:h-80 lg:w-96 lg:h-96 rounded-full object-cover shadow-[0_8px_24px_-12px_rgba(0,0,0,0.18)]"
                      sizes="(max-width: 640px) 288px, (max-width: 1024px) 320px, 384px"
                      priority
                    />
                  </FloatingElement>
                </div>
                <div className="mt-5 text-center">
                  <p className="font-heading font-semibold text-[22px] text-[#1F2937]">Prof. Dr. Nilüfer Yıldırım</p>
                  <p className="text-[14px] text-[#5F6B76] mt-1">Bütüncül Sağlık & Fonksiyonel Tıp</p>
                  <div className="flex items-center justify-center gap-3 mt-3">
                    {settings.social?.youtube && (
                      <a href={settings.social.youtube} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-[#F5F3EF] flex items-center justify-center hover:bg-[#1F6E7A] hover:text-white text-[#5F6B76] transition-colors" aria-label="YouTube">
                        <Youtube size={22} />
                      </a>
                    )}
                    {settings.social?.instagram && (
                      <a href={settings.social.instagram} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-[#F5F3EF] flex items-center justify-center hover:bg-[#1F6E7A] hover:text-white text-[#5F6B76] transition-colors" aria-label="Instagram">
                        <Instagram size={22} />
                      </a>
                    )}
                    {settings.social?.facebook && (
                      <a href={settings.social.facebook} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-[#F5F3EF] flex items-center justify-center hover:bg-[#1F6E7A] hover:text-white text-[#5F6B76] transition-colors" aria-label="Facebook">
                        <Facebook size={22} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Hero Banner Marquee */}
      {(settings.hero_banner?.items?.length > 0) && (
      <div className="w-full overflow-hidden border-y border-[#D8C7A6]/30 bg-[#F5F3EF] py-4 sm:py-5" data-testid="hero-banner-marquee">
        {/* Desktop: static centered */}
        <div className="hidden sm:flex items-center justify-center gap-14 px-4">
          {(settings.hero_banner.items).map((item, i) => {
            const text = typeof item === 'string' ? item : item.text;
            const iconName = typeof item === 'string' ? 'Award' : (item.icon || 'Award');
            const IconComp = getBannerIcon(iconName);
            return (
              <div key={i} className="flex items-center gap-3 whitespace-nowrap">
                <div className="w-8 h-8 rounded-full bg-[#1F6E7A]/10 flex items-center justify-center shrink-0">
                  <IconComp size={16} className="text-[#1F6E7A]" />
                </div>
                <span className="text-base font-semibold text-[#1F2937] tracking-tight">{text}</span>
              </div>
            );
          })}
        </div>
        {/* Mobile: scrolling marquee */}
        <div className="sm:hidden relative">
          <div className="flex animate-marquee gap-12 w-max">
            {[...(settings.hero_banner.items), ...(settings.hero_banner.items), ...(settings.hero_banner.items)].map((item, i) => {
              const text = typeof item === 'string' ? item : item.text;
              const iconName = typeof item === 'string' ? 'Award' : (item.icon || 'Award');
              const IconComp = getBannerIcon(iconName);
              return (
                <div key={i} className="flex items-center gap-3 whitespace-nowrap">
                  <div className="w-7 h-7 rounded-full bg-[#1F6E7A]/10 flex items-center justify-center shrink-0">
                    <IconComp size={14} className="text-[#1F6E7A]" />
                  </div>
                  <span className="text-sm font-semibold text-[#1F2937] tracking-tight">{text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      )}

      {/* C) 3 Service Cards */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 sm:py-20" data-testid="home-services-section">
        <Reveal variant="subtle" duration={0.6}>
          <div className="text-center mb-10">
            <h2 className="font-heading text-[30px] sm:text-[28px] font-semibold tracking-[-0.01em] mb-3">{settings.homepage_sections?.services_title || 'Tedavilerimiz'}</h2>
            <p className="text-[14px] sm:text-[22px] text-[#5F6B76] max-w-xl mx-auto">{settings.homepage_sections?.services_subtitle || 'Size en uygun programı birlikte belirleyelim.'}</p>
          </div>
        </Reveal>
        <div className="space-y-6 max-w-5xl mx-auto">
          {services.map((s, i) => (
            <Reveal key={s.id} variant="fadeUp" delay={i * 100}>
              <ServiceCard service={s} index={i} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* D) Health Assessment */}
      <section className="py-16 sm:py-20 bg-[#F5F3EF]" data-testid="home-symptoms-carousel">
        <Reveal variant="subtle" duration={0.6}>
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 mb-8">
            <h2 className="font-heading text-[30px] sm:text-[28px] font-semibold tracking-[-0.01em] mb-2">{settings.homepage_sections?.assessment_title || 'Sizin için hangi tedavi hizmeti daha uygun?'}</h2>
            <p className="text-[14px] sm:text-[22px] text-[#5F6B76] max-w-2xl">{settings.homepage_sections?.assessment_subtitle || 'Belirtilerinizi 1-5 arası puanlayın, genel değerlendirme ile size en uygun hizmeti anında görün.'}</p>
          </div>
        </Reveal>
        <Reveal variant="fadeUp" delay={100}>
          <HealthAssessment />
        </Reveal>
      </section>

      {/* Reels / YouTube Shorts */}
      {(settings.reels?.items?.length > 0) && (
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-12 sm:py-16" data-testid="home-reels-section">
        <Reveal variant="subtle" duration={0.6}>
          <div className="text-center mb-8">
            <h2 className="font-heading text-[30px] sm:text-[28px] font-semibold tracking-[-0.01em] mb-2">{settings.reels?.title || 'Kısa İçerikler'}</h2>
            <p className="text-[14px] sm:text-[22px] text-[#5F6B76]">{settings.reels?.subtitle || 'Sağlık ipuçları ve bilgilendirici kısa videolar'}</p>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {settings.reels.items.map((reel, i) => {
            const url = reel.url || '';
            const shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/);
            const watchMatch = url.match(/[?&]v=([a-zA-Z0-9_-]+)/);
            const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
            const videoId = shortsMatch?.[1] || watchMatch?.[1] || shortMatch?.[1] || reel.id;
            if (!videoId) return null;
            return (
              <Reveal key={videoId} variant="fadeUp" delay={i * 80}>
                <div className="rounded-2xl overflow-hidden bg-black aspect-[9/16] shadow-md">
                  <iframe
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title={`Reels ${i + 1}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                    loading="eager"
                  />
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>
      )}

      {/* E) Process 4 Steps — Timeline */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 sm:py-20" data-testid="home-process-steps">
        <Reveal variant="subtle" duration={0.6}>
          <div className="text-center mb-12">
            <h2 className="font-heading text-[30px] sm:text-[28px] font-semibold tracking-[-0.01em] mb-3">{settings.homepage_sections?.process_title || '4 Adımda Sağlık Yolculuğu'}</h2>
          </div>
        </Reveal>

        {/* Desktop timeline */}
        <div className="hidden lg:flex items-start justify-center gap-0 relative">
          {processSteps.map((step, i) => {
            const IconComp = processIcons[step.icon] || MessageCircle;
            return (
              <Reveal key={step.step} variant="fadeUp" delay={i * 120}>
                <div className="flex items-start">
                  <div className="flex flex-col items-center" style={{ width: 200 }}>
                    <motion.div
                      className="w-16 h-16 rounded-full bg-white border-2 border-[#1F6E7A]/30 flex items-center justify-center shadow-[0_4px_16px_-4px_rgba(31,110,122,0.15)]"
                      whileHover={{ scale: 1.12, borderColor: '#1F6E7A' }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <IconComp size={26} className="text-[#1F6E7A]" />
                    </motion.div>
                    <div className="text-[13px] font-semibold text-[#1F6E7A]/60 mt-3 mb-1">Adım {step.step}</div>
                    <h3 className="font-heading font-semibold text-[14px] text-center leading-snug">{step.title}</h3>
                  </div>
                  {i < processSteps.length - 1 && (
                    <div className="flex items-center h-16 px-2">
                      <div className="w-16 h-[2px] bg-gradient-to-r from-[#1F6E7A]/30 to-[#1F6E7A]/15" />
                      <ChevronRight size={24} className="text-[#1F6E7A]/40 -ml-1" />
                    </div>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Mobile timeline — vertical */}
        <div className="lg:hidden flex flex-col relative ml-6">
          {/* Vertical line */}
          <div className="absolute top-5 bottom-5 left-[18px] w-[2px] bg-gradient-to-b from-[#1F6E7A]/30 to-[#1F6E7A]/10" />

          {processSteps.map((step, i) => {
            const IconComp = processIcons[step.icon] || MessageCircle;
            return (
              <Reveal key={step.step} variant="fadeRight" delay={i * 100}>
                <div className="flex items-center gap-5 py-4">
                  <div className="w-[38px] h-[38px] min-w-[38px] rounded-full bg-white border-2 border-[#1F6E7A]/30 flex items-center justify-center shadow-sm z-10">
                    <IconComp size={16} className="text-[#1F6E7A]" />
                  </div>
                  <div>
                    <span className="text-[12px] font-semibold text-[#1F6E7A]/60 block">Adım {step.step}</span>
                    <h3 className="font-heading font-semibold text-[14px] leading-snug">{step.title}</h3>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal variant="fadeUp" delay={400}>
          <div className="text-center mt-10">
            <Link
              to="/surec"
              className="inline-flex items-center gap-2 text-[14px] font-semibold text-[#1F6E7A] hover:gap-3 transition-all"
            >
              Süreci detaylı incele <ArrowRight size={18} />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* Sade Düşün Section */}
      <section className="pt-10 sm:pt-16 pb-12 sm:pb-16" data-testid="home-consulting-section">
        <Reveal variant="fadeUp" duration={0.7}>
          <div className="flex justify-center mb-8 px-0 sm:px-4">
            <img
              src="/assets/sade-dusun-banner.png"
              alt="Dr. Nilüfer Yıldırım'la Sade Düşün"
              className="w-full sm:max-h-[300px] sm:w-auto object-contain"
              data-testid="sade-dusun-banner-image"
            />
          </div>
        </Reveal>
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-10">
          <Reveal variant="fadeRight" duration={0.7}>
            <div className="bg-white/80 backdrop-blur-sm rounded-[24px] border border-[#E7E5E4]/60 p-8 sm:p-10">
              <div>
                <h2 className="font-heading text-[24px] sm:text-[22px] font-semibold text-[#1F2937] mb-3">{settings.sade_dusun?.title || 'Sade Düşün, Sade Yaşa'}</h2>
                <p className="text-[14px] sm:text-[22px] text-[#5F6B76] leading-relaxed mb-4">
                  {settings.sade_dusun?.description || 'SADE felsefesiyle — Sağlıklı, Aktif, Dengeli, Esnek — yaşamınızı yeniden şekillendirin. Bilimsel temelli fonksiyonel tıp yaklaşımı ile kişiye özel danışmanlık programı.'}
                </p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {['Sağlıklı', 'Aktif', 'Dengeli', 'Esnek'].map((w, i) => (
                    <span key={i} className="text-xs px-3 py-1 rounded-full bg-[#F5F3EF] text-[#5F6B76] border border-[#E7E5E4]/60 font-medium">{w}</span>
                  ))}
                </div>
                <Link
                  to="/danismanlik"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F6E7A] hover:text-[#185960] transition-colors"
                  data-testid="home-consulting-link"
                >
                  {settings.sade_dusun?.link_text || 'Danışmanlık hakkında bilgi alın'} <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <section className="py-16 sm:py-20 bg-[#F5F3EF]" data-testid="home-video-strip">
        <Reveal variant="subtle" duration={0.6}>
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading text-[30px] sm:text-[28px] font-semibold tracking-[-0.01em] mb-2">{settings.homepage_sections?.videos_title || 'Video İçerikler'}</h2>
                <p className="text-[14px] text-[#5F6B76]">{settings.homepage_sections?.videos_subtitle || 'Fonksiyonel tıp hakkında bilgilendirici videolar.'}</p>
              </div>
              <a
                href="https://www.youtube.com/@dr.niluferyldrmlasadedusun9263"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F6E7A] hover:gap-2.5 transition-all"
              >
                YouTube'da İzle <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </Reveal>
        <Reveal variant="fadeUp" delay={100}>
          <div className="relative group">
            {/* Left scroll button */}
            <button
              onClick={() => {
                const el = document.getElementById('video-gallery');
                if (el) el.scrollBy({ left: -620, behavior: 'smooth' });
              }}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/90 border border-[#E7E5E4] shadow-lg flex items-center justify-center text-[#1F6E7A] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#1F6E7A] hover:text-white"
              data-testid="video-scroll-left"
            >
              <ChevronLeft size={20} />
            </button>
            {/* Right scroll button */}
            <button
              onClick={() => {
                const el = document.getElementById('video-gallery');
                if (el) el.scrollBy({ left: 620, behavior: 'smooth' });
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/90 border border-[#E7E5E4] shadow-lg flex items-center justify-center text-[#1F6E7A] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#1F6E7A] hover:text-white"
              data-testid="video-scroll-right"
            >
              <ChevronRight size={20} />
            </button>
            <div id="video-gallery" className="flex gap-4 overflow-x-auto hide-scrollbar px-4 sm:px-6 lg:px-10 snap-x snap-mandatory pb-4 scroll-smooth">
            {videos.map((v) => (
              <motion.div
                key={v.id}
                className="flex-shrink-0 w-[280px] sm:w-[300px] bg-white rounded-[20px] overflow-hidden border border-[#E7E5E4] snap-start"
                whileHover={{ y: -4, boxShadow: '0 16px 32px -16px rgba(0,0,0,0.18)' }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                data-testid="video-card"
              >
                <div className="relative aspect-video bg-gray-100 overflow-hidden">
                  <motion.img
                    src={`https://img.youtube.com/vi/${v.id}/mqdefault.jpg`}
                    alt={v.title}
                    className="w-full h-full object-cover"
                    loading="eager"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.4 }}
                  />
                  <a
                    href={`https://www.youtube.com/watch?v=${v.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${v.title} videosunu izle`}
                    className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors"
                  >
                    <motion.div
                      className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg"
                      whileHover={{ scale: 1.15 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Play size={20} className="text-[#1F6E7A] ml-0.5" fill="currentColor" />
                    </motion.div>
                  </a>
                </div>
                <div className="p-4">
                  <h3 className="text-[14px] font-medium text-[#1F2937] line-clamp-2">{v.title}</h3>
                  <p className="text-xs text-[#5F6B76] mt-1">{v.category}</p>
                </div>
              </motion.div>
            ))}
            </div>
          </div>
        </Reveal>
        <div className="sm:hidden text-center mt-4">
          <a
            href="https://www.youtube.com/@dr.niluferyldrmlasadedusun9263"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F6E7A]"
          >
            YouTube'da İzle <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* G) Blog Picks */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 sm:py-20" data-testid="home-blog-picks">
        <Reveal variant="subtle" duration={0.6}>
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-heading text-[30px] sm:text-[28px] font-semibold tracking-[-0.01em] mb-2">{settings.homepage_sections?.blog_title || 'İçeriklerden Seçmeler'}</h2>
              <p className="text-[14px] text-[#5F6B76]">{settings.homepage_sections?.blog_subtitle || 'Sağlık ve yaşam kalitesi üzerine yazılar.'}</p>
            </div>
            <Link
              to="/icerikler"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F6E7A] hover:gap-2.5 transition-all"
            >
              Tüm yazılar <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {blogs.map((post, i) => (
            <Reveal key={post.id} variant="fadeUp" delay={i * 100}>
              <HoverCard className="h-full">
                <Link
                  to={`/icerikler/${post.id}`}
                  className="block bg-white rounded-[28px] border border-[#E7E5E4] overflow-hidden h-full"
                  data-testid="blog-card"
                >
                  <div className="aspect-video bg-gray-100 overflow-hidden">
                    <motion.img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover"
                      loading="eager"
                      whileHover={{ scale: 1.08 }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-[#1F6E7A] bg-[#1F6E7A]/8 px-2 py-0.5 rounded-full">{post.category}</span>
                      <span className="text-xs text-[#5F6B76] flex items-center gap-1"><Clock size={12} /> {post.reading_time}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-[22px] mb-2 line-clamp-2">{post.title}</h3>
                    <p className="text-[14px] text-[#5F6B76] line-clamp-2">{post.summary}</p>
                  </div>
                </Link>
              </HoverCard>
            </Reveal>
          ))}
        </div>
        <div className="sm:hidden text-center mt-6">
          <Link to="/icerikler" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F6E7A]">
            Tüm yazılar <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Kurumsal Davet CTA */}
      <section className="py-12 sm:py-16 bg-[#F5F3EF]" data-testid="home-corporate-cta">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 text-center">
          <Reveal variant="fadeUp" duration={0.7}>
            <div className="flex justify-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center shadow-sm"><Tv size={16} className="text-[#1F6E7A]" /></div>
              <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center shadow-sm"><Mic size={16} className="text-[#1F6E7A]" /></div>
              <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center shadow-sm"><Users size={16} className="text-[#1F6E7A]" /></div>
            </div>
            <h2 className="font-heading text-[24px] sm:text-[22px] font-semibold text-[#1F2937] mb-3">{settings.corporate_cta?.title || 'Konferans, Seminer veya Medya Daveti'}</h2>
            <p className="text-[14px] sm:text-[22px] text-[#5F6B76] leading-relaxed mb-6">{settings.corporate_cta?.description || 'TV programları, kurumsal seminerler, kongreler veya eğitim etkinlikleri için Prof. Dr. Nilüfer Yıldırım\'ı davet etmek isterseniz bizimle iletişime geçin.'}</p>
            <Link
              to="/iletisim"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1F6E7A] text-white text-sm font-medium hover:bg-[#185960] transition-colors shadow-sm"
              data-testid="corporate-cta-button"
            >
              <Mail size={16} />
              {settings.corporate_cta?.button_text || 'İletişime Geçin'}
            </Link>
          </Reveal>
        </div>
      </section>

      {/* H) News & Congress Timeline */}
      <section className="py-16 sm:py-20 bg-[#F5F3EF]" data-testid="home-timeline">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
          <Reveal variant="subtle" duration={0.6}>
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-heading text-[30px] sm:text-[28px] font-semibold tracking-[-0.01em] mb-2">{settings.homepage_sections?.news_title || 'Haberler & Kongreler'}</h2>
                <p className="text-[14px] text-[#5F6B76]">{settings.homepage_sections?.news_subtitle || 'Güncel gelişmeler ve katılımlar.'}</p>
              </div>
              <Link to="/haberler" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F6E7A] hover:gap-2.5 transition-all">
                Tümü <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
          <div className="space-y-4">
            {news.slice(0, 3).map((n, i) => (
              <Reveal key={n.id} variant="fadeUp" delay={i * 80}>
                <motion.div
                  className="bg-white rounded-2xl border border-[#E7E5E4] overflow-hidden flex flex-col sm:flex-row"
                  whileHover={{ y: -4, boxShadow: '0 12px 32px -12px rgba(0,0,0,0.12)' }}
                  transition={{ type: 'spring', stiffness: 300 }}
                  data-testid="timeline-entry-card"
                >
                  {n.image && (
                    <div className="sm:w-56 sm:shrink-0 aspect-[16/9] sm:aspect-auto overflow-hidden">
                      <OptimizedImage src={n.image} alt={n.title} className="w-full h-full object-cover" sizes="(max-width: 640px) 100vw, 224px" />
                    </div>
                  )}
                  <div className="p-4 flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-[#1F6E7A] bg-[#1F6E7A]/8 px-2 py-0.5 rounded-full">{n.type}</span>
                      <span className="text-xs text-[#5F6B76]">{formatDateTR(n.date)}</span>
                    </div>
                    <h3 className="text-[14px] font-semibold text-[#1F2937]">{n.title}</h3>
                    <p className="text-[14px] text-[#5F6B76] mt-1.5">{n.description}</p>
                  </div>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* I) Certificates Timeline */}
      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-10 py-16 sm:py-20" data-testid="home-certificates">
        <Reveal variant="subtle" duration={0.6}>
          <div className="text-center mb-10">
            <h2 className="font-heading text-[30px] sm:text-[28px] font-semibold tracking-[-0.01em] mb-3">{settings.homepage_sections?.certs_title || 'Diploma & Sertifikalar'}</h2>
            <p className="text-[14px] sm:text-[22px] text-[#5F6B76] max-w-xl mx-auto">{settings.homepage_sections?.certs_subtitle || 'Akademik ve profesyonel yetkinlikler.'}</p>
          </div>
        </Reveal>
        <div className="relative">
          <div className="absolute left-[18px] sm:left-[22px] top-0 bottom-0 w-px bg-[#E7E5E4]" />
          {[...certificates].sort((a, b) => (parseInt(b.year) || 0) - (parseInt(a.year) || 0)).slice(0, 8).map((c, i) => {
            const IconComp = certIcons[c.type] || Award;
            return (
              <Reveal key={c.id} variant="fadeUp" delay={i * 60}>
                <div className="relative flex items-start gap-4 sm:gap-5 mb-6 last:mb-0 pl-1">
                  <div className="relative z-10 w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-white border border-[#E7E5E4] flex items-center justify-center shrink-0 shadow-sm">
                    <IconComp size={16} className="text-[#D8C7A6]" />
                  </div>
                  <div className="flex-1 pt-1">
                    <p className="text-[14px] font-semibold text-[#D8C7A6] mb-0.5">{c.year}</p>
                    <h3 className="text-base font-semibold text-[#1F2937]">{c.title}</h3>
                    <p className="text-[14px] text-[#5F6B76] mt-0.5">{c.institution}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal variant="fadeUp" delay={300}>
          <div className="text-center mt-8">
            <Link
              to="/hakkimizda"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F6E7A] hover:gap-2.5 transition-all"
            >
              Tüm sertifikalar <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* J) Mini FAQ */}
      <section className="py-16 sm:py-20 bg-[#F5F3EF]" data-testid="home-faq">
        <div className="mx-auto max-w-2xl px-4 sm:px-6">
          <Reveal variant="subtle" duration={0.6}>
            <div className="text-center mb-10">
              <h2 className="font-heading text-[30px] sm:text-[28px] font-semibold tracking-[-0.01em] mb-3">{settings.homepage_sections?.faq_title || 'Sık Sorulan Sorular'}</h2>
              <p className="text-[14px] text-[#5F6B76]">{settings.homepage_sections?.faq_subtitle || 'Sık karşılaştığımız sorular ve yanıtlarını aşağıda bulabilirsiniz.'}</p>
            </div>
          </Reveal>
          <Reveal variant="fadeUp" delay={100}>
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {faq.map((item, i) => (
                <AccordionItem
                    key={item.id || `faq-${i}`}
                    value={`faq-${i}`}
                    className="bg-white rounded-[20px] border border-[#E7E5E4] px-5 overflow-hidden data-[state=open]:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.08)] data-[state=open]:border-[#1F6E7A]/30 transition-all"
                  >
                    <AccordionTrigger className="text-[14px] font-semibold text-left py-5 hover:no-underline [&[data-state=open]>svg]:rotate-45">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[14px] text-[#5F6B76] leading-relaxed pb-5">
                      {item.a}
                    </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* K) Contact Block */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 sm:py-20" data-testid="home-contact-block">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <Reveal variant="fadeLeft">
            <div>
              <h2 className="font-heading text-[30px] sm:text-[28px] font-semibold tracking-[-0.01em] mb-3">{settings.homepage_sections?.contact_title || 'Randevu & İletişim'}</h2>
              <p className="text-[14px] text-[#5F6B76] leading-relaxed mb-6">
                {settings.homepage_sections?.contact_subtitle || 'Sorularınız ve randevu talepleriniz için aşağıdaki formu doldurun veya doğrudan iletişime geçin.'}
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3 text-[14px] text-[#5F6B76]">
                  <MapPin size={16} className="mt-0.5 shrink-0 text-[#D8C7A6]" />
                  <span>{settings.contact?.address}</span>
                </div>
                <div className="flex items-start gap-3 text-[14px] text-[#5F6B76]">
                  <Clock size={16} className="mt-0.5 shrink-0 text-[#D8C7A6]" />
                  <div>
                    {settings.contact?.schedule ? (
                      formatWorkingHours(settings.contact.schedule)?.map((g, i) => (
                        <p key={i}>
                          {g.days.length > 1 ? `${g.days[0]} – ${g.days[g.days.length - 1]}` : g.days[0]}
                          {': '}
                          {g.open ? `${g.start} – ${g.end}` : 'Kapalı'}
                        </p>
                      ))
                    ) : (
                      <span>{settings.contact?.hours}</span>
                    )}
                  </div>
                </div>
              </div>
              <motion.a
                href={`https://wa.me/${settings.contact?.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#1F6E7A] text-[#1F6E7A] text-sm font-medium transition-colors"
                whileHover={{ scale: 1.04, backgroundColor: 'rgba(31,110,122,0.05)' }}
                whileTap={{ scale: 0.96 }}
              >
                <MessageCircle size={16} /> WhatsApp'ı Tercih Edin
              </motion.a>
            </div>
          </Reveal>
          <Reveal variant="fadeRight" delay={100}>
            <div className="bg-white rounded-[28px] border border-[#E7E5E4] p-6 sm:p-8 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.08)]">
              <ContactForm compact />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
